import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AtSign, CalendarClock, Clock3, ListFilter, MessageSquareText, X } from "lucide-react";
import { createPortal } from "react-dom";

import {
  addTaskRemarkV2,
  createTaskV2,
  deleteTaskV2,
  getAllTasks,
  getTaskProjectSuggestions,
  getTaskRemarks,
  getTaskUserSuggestions,
  updateTaskV2,
} from "@/lib/api";
import { parseTaggingReferences } from "@/lib/tagging";
import AppShell from "@/components/AppShell";
import { TaggingInput } from "@/components/TaggingInput";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";

const statusChipClass = {
  open: "jump-chip",
  in_progress: "jump-chip jump-chip-watch",
  blocked: "jump-chip jump-chip-critical",
  completed: "jump-chip jump-chip-healthy",
};

const rowPriorityClass = {
  high: "row-prio row-prio-high",
  medium: "row-prio row-prio-medium",
  low: "row-prio row-prio-low",
};

const priorityOptionMeta = {
  high: { label: "High", chipClass: "chip chip-crit", dot: "#DC2626" },
  medium: { label: "Medium", chipClass: "chip chip-warn", dot: "#D97706" },
  low: { label: "Low", chipClass: "chip chip-mute", dot: "#64748B" },
};

const statusOptionMeta = {
  open: { label: "Open", chipClass: "chip chip-mute", dot: "#64748B" },
  in_progress: { label: "In progress", chipClass: "chip chip-brand", dot: "#4F46E5" },
  blocked: { label: "Blocked", chipClass: "chip chip-crit", dot: "#B91C1C" },
  completed: { label: "Completed", chipClass: "chip chip-ok", dot: "#047857" },
};

const GENERAL_TASK_PROJECT_ID = "__general__";

const MetaChip = ({ item, testId }) => (
  <span className={item.chipClass} style={{ lineHeight: "18px" }} data-testid={testId}>
    <span style={{ width: 7, height: 7, borderRadius: 99, background: item.dot, display: "inline-block" }} />
    {item.label}
  </span>
);

const toDate = (value) => {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const isOverdue = (task) => {
  if (task.status === "completed") return false;
  const due = toDate(task.deadline);
  if (!due) return false;
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return due < now;
};

const initialComposer = {
  title_markup: "",
  priority: "medium",
  due_date: "",
  assignee_email: "",
  project_id: GENERAL_TASK_PROJECT_ID,
  notes: "",
};

const priorityOrder = { high: 0, medium: 1, low: 2 };

const formatTaskDateTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" }) +
    " at " +
    date.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }).toLowerCase();
};

const displayActor = (email = "") => {
  const local = String(email || "").split("@")[0] || "";
  const tokens = local.split(/[._-]/).filter(Boolean);
  return tokens.length ? tokens.map((token) => token.charAt(0).toUpperCase() + token.slice(1)).slice(0, 2).join(" ") : email;
};

const mentionNamesFromText = (text) => {
  const values = (text || "").match(/@([A-Za-z][A-Za-z0-9_-]*)/g) || [];
  const unique = [];
  const seen = new Set();
  values.forEach((value) => {
    const clean = value.slice(1).trim();
    if (!clean) return;
    const firstName = clean.split("_")[0].split("-")[0];
    const normalized = firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase();
    if (!seen.has(normalized.toLowerCase())) {
      seen.add(normalized.toLowerCase());
      unique.push(normalized);
    }
  });
  return unique;
};

const daysDeltaLabel = (deadline) => {
  const date = toDate(deadline);
  if (!date) return "";
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diffDays = Math.round((date - today) / (1000 * 60 * 60 * 24));
  if (diffDays < 0) return `${diffDays}d`;
  if (diffDays === 0) return "0d";
  return `+${diffDays}d`;
};

const formatDisplayDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

const getTaskCompletionReference = (task) => task.closed_at || task.updated_at || task.created_at || null;

const computeGroupHealth = (rows) => {
  const total = rows.length || 1;
  const done = rows.filter((row) => row.status === "completed").length;
  const overdue = rows.filter((row) => isOverdue(row)).length;
  const score = Math.max(0, Math.min(100, Math.round((done / total) * 70 + ((total - overdue) / total) * 30)));
  return score;
};

export default function FullTasksPage({ user, onLogout }) {
  const navigate = useNavigate();

  const canCreateTask = Boolean(user?.email);

  const [scopeTab, setScopeTab] = useState("all");
  const [tasks, setTasks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState("all");
  const [activeSort, setActiveSort] = useState("due_date");
  const [expandedGroups, setExpandedGroups] = useState({});
  const [isSavingComposer, setIsSavingComposer] = useState(false);
  const [composer, setComposer] = useState(initialComposer);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);

  const [drawerTask, setDrawerTask] = useState(null);
  const [drawerRemarks, setDrawerRemarks] = useState([]);
  const [drawerNoteDraft, setDrawerNoteDraft] = useState("");
  const [isPostingRemark, setIsPostingRemark] = useState(false);
  const [drawerDraft, setDrawerDraft] = useState({
    task: "",
    status: "open",
    priority: "medium",
    assigned_to_email: "",
    deadline: "",
    notes: "",
  });

  const noteTextareaRef = useRef(null);
  const mentionAnchorRef = useRef(null);
  const [mentionQuery, setMentionQuery] = useState("");
  const [mentionDropdownOpen, setMentionDropdownOpen] = useState(false);
  const [mentionDropdownPos, setMentionDropdownPos] = useState({ top: 0, left: 0, width: 260 });
  const [isDrawerEditMode, setIsDrawerEditMode] = useState(false);

  const loadMeta = async () => {
    try {
      const [usersResult, projectsResult] = await Promise.allSettled([
        getTaskUserSuggestions({ q: "", workspace_domain: user.workspace_domain }),
        getTaskProjectSuggestions({ q: "", workspace_domain: user.workspace_domain }),
      ]);

      const usersRes = usersResult.status === "fulfilled" ? usersResult.value : [];
      const projectsRes = projectsResult.status === "fulfilled" ? projectsResult.value : [];

      setUsers(usersRes);
      setProjects(projectsRes);

      if (!composer.assignee_email && usersRes.length > 0) {
        setComposer((prev) => ({ ...prev, assignee_email: usersRes[0].email }));
      }
      if (!composer.project_id && projectsRes.length > 0) {
        setComposer((prev) => ({ ...prev, project_id: projectsRes[0].id }));
      } else if (!composer.project_id) {
        setComposer((prev) => ({ ...prev, project_id: GENERAL_TASK_PROJECT_ID }));
      }
    } catch (_error) {
      // silent; suggestions are optional helper metadata
    }
  };

  const loadTasks = async (selectedScope = scopeTab) => {
    setIsLoading(true);
    try {
      const rows = await getAllTasks({
        assigned_scope: selectedScope,
        limit: 1000,
      });
      setTasks(rows);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadMeta();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadTasks(scopeTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeTab]);

  const taskStats = useMemo(() => {
    const open = tasks.filter((task) => task.status !== "completed").length;
    const overdue = tasks.filter((task) => isOverdue(task)).length;
    const weekLater = new Date();
    weekLater.setDate(weekLater.getDate() + 7);
    weekLater.setHours(23, 59, 59, 999);
    const dueWeek = tasks.filter((task) => {
      if (task.status === "completed") return false;
      const due = toDate(task.deadline);
      if (!due) return false;
      const now = new Date();
      now.setHours(0, 0, 0, 0);
      return due >= now && due <= weekLater;
    }).length;
    const onTrack = Math.max(0, open - overdue);
    return { open, overdue, dueWeek, onTrack };
  }, [tasks]);

  const filteredTasks = useMemo(() => {
    if (activeFilter === "all") return tasks;
    if (activeFilter === "assigned") return tasks.filter((task) => task.assigned_to === user.email);
    if (activeFilter === "overdue") return tasks.filter((task) => isOverdue(task));
    if (activeFilter === "high") return tasks.filter((task) => task.priority === "high");
    if (activeFilter === "created") return tasks.filter((task) => task.assigned_by === user.email);
    return tasks;
  }, [activeFilter, tasks, user.email]);

  const activeFilteredTasks = useMemo(
    () => filteredTasks.filter((task) => task.status !== "completed"),
    [filteredTasks],
  );

  const completedTasksLast30Days = useMemo(() => {
    const cutoff = Date.now() - 30 * 24 * 60 * 60 * 1000;
    return filteredTasks
      .filter((task) => task.status === "completed")
      .filter((task) => {
        const completedAt = toDate(getTaskCompletionReference(task));
        if (!completedAt) return false;
        return completedAt.getTime() >= cutoff;
      })
      .sort((a, b) => String(getTaskCompletionReference(b) || "").localeCompare(String(getTaskCompletionReference(a) || "")));
  }, [filteredTasks]);

  const groupedTasks = useMemo(() => {
    const map = new Map();
    activeFilteredTasks.forEach((task) => {
      const key = task.project_id;
      const existing = map.get(key) || {
        project_id: task.project_id,
        project_name: task.project_name,
        client_name: task.client_name,
        rows: [],
      };
      existing.rows.push(task);
      map.set(key, existing);
    });

    return [...map.values()]
      .map((group) => {
        const sortedRows = [...group.rows].sort((a, b) => {
          if (activeSort === "priority") {
            return (priorityOrder[a.priority || "medium"] ?? 99) - (priorityOrder[b.priority || "medium"] ?? 99);
          }
          const aDate = toDate(a.deadline)?.getTime() || Number.MAX_SAFE_INTEGER;
          const bDate = toDate(b.deadline)?.getTime() || Number.MAX_SAFE_INTEGER;
          return aDate - bDate;
        });
        return {
          ...group,
          rows: sortedRows,
          overdue_count: sortedRows.filter((row) => isOverdue(row)).length,
          health_score: computeGroupHealth(sortedRows),
        };
      })
      .sort((a, b) => b.overdue_count - a.overdue_count);
  }, [activeFilteredTasks, activeSort]);

  const completedGroups = useMemo(() => {
    const map = new Map();
    completedTasksLast30Days.forEach((task) => {
      const key = task.project_id;
      const existing = map.get(key) || {
        project_id: task.project_id,
        project_name: task.project_name,
        client_name: task.client_name,
        rows: [],
      };
      existing.rows.push(task);
      map.set(key, existing);
    });

    return [...map.values()].sort((a, b) => b.rows.length - a.rows.length);
  }, [completedTasksLast30Days]);

  const teamMemberMentions = useMemo(() => {
    const map = new Map();

    users.forEach((member) => {
      const name = member.name || displayActor(member.email);
      const firstName = (name.split(" ")[0] || name || "Team").replace(/[^A-Za-z]/g, "") || "Team";
      map.set(member.email || firstName, {
        id: member.email || firstName,
        name,
        firstName,
        mention: `@${firstName}`,
      });
    });

    tasks.forEach((task) => {
      const email = task.assigned_to;
      if (!email) return;
      const name = displayActor(email);
      const firstName = (name.split(" ")[0] || name || "Team").replace(/[^A-Za-z]/g, "") || "Team";
      if (!map.has(email)) {
        map.set(email, {
          id: email,
          name,
          firstName,
          mention: `@${firstName}`,
        });
      }
    });

    if (!map.has(user.email)) {
      const name = displayActor(user.email);
      const firstName = (name.split(" ")[0] || name || "Team").replace(/[^A-Za-z]/g, "") || "Team";
      map.set(user.email, {
        id: user.email,
        name,
        firstName,
        mention: `@${firstName}`,
      });
    }

    return [...map.values()];
  }, [tasks, user.email, users]);

  const filteredMentionMembers = useMemo(() => {
    const query = mentionQuery.trim().toLowerCase();
    if (!query) return teamMemberMentions.slice(0, 8);
    return teamMemberMentions.filter((member) => member.firstName.toLowerCase().startsWith(query) || member.name.toLowerCase().includes(query)).slice(0, 8);
  }, [mentionQuery, teamMemberMentions]);

  useEffect(() => {
    setExpandedGroups((prev) => {
      const next = { ...prev };
      groupedTasks.forEach((group) => {
        if (!(group.project_id in next)) {
          next[group.project_id] = true;
        }
      });
      return next;
    });
  }, [groupedTasks]);

  const dueDateHistory = useMemo(() => {
    if (!drawerTask?.due_date_history) return [];
    return [...drawerTask.due_date_history].sort((a, b) => String(b.changed_at || "").localeCompare(String(a.changed_at || "")));
  }, [drawerTask?.due_date_history]);

  const orderedRemarks = useMemo(() => {
    const rows = drawerRemarks.length ? drawerRemarks : drawerTask?.remarks || [];
    return [...rows].sort((a, b) => String(a.created_at || a.at || "").localeCompare(String(b.created_at || b.at || "")));
  }, [drawerRemarks, drawerTask?.remarks]);

  const memberLabelMap = useMemo(() => {
    const map = new Map();
    users.forEach((member) => {
      if (member.email) map.set(member.email, member.name || displayActor(member.email));
    });
    return map;
  }, [users]);

  const composerRefs = useMemo(() => parseTaggingReferences(composer.title_markup), [composer.title_markup]);

  useEffect(() => {
    if (composerRefs.lastUserMention?.id && !(composer.project_id === GENERAL_TASK_PROJECT_ID && composer.assignee_email === "__all__")) {
      setComposer((prev) => ({ ...prev, assignee_email: composerRefs.lastUserMention.id }));
    }
    if (composerRefs.lastProjectMention?.id) {
      setComposer((prev) => ({ ...prev, project_id: composerRefs.lastProjectMention.id }));
    }
  }, [composerRefs.lastProjectMention?.id, composerRefs.lastUserMention?.id, composer.project_id, composer.assignee_email]);

  useEffect(() => {
    if (composer.project_id !== GENERAL_TASK_PROJECT_ID && composer.assignee_email === "__all__") {
      setComposer((prev) => ({ ...prev, assignee_email: users[0]?.email || "" }));
    }
  }, [composer.project_id, composer.assignee_email, users]);

  const createInlineTask = async () => {
    if (!canCreateTask) {
      toast.error("You do not have permission to create tasks");
      return;
    }
    if (!composerRefs.title) {
      toast.error("Task title is required");
      return;
    }
    if (!composer.due_date) {
      toast.error("Due date is required");
      return;
    }

    setIsSavingComposer(true);
    try {
      const assignToAll = composer.project_id === GENERAL_TASK_PROJECT_ID && composer.assignee_email === "__all__";
      await createTaskV2({
        project_id: composer.project_id || GENERAL_TASK_PROJECT_ID,
        task: composerRefs.title,
        assigned_to_email: assignToAll ? undefined : (composer.assignee_email || undefined),
        assign_to_all: assignToAll,
        deadline: composer.due_date,
        priority: composer.priority,
        notes: composer.notes,
      });
      toast.success("Task added");
      setComposer((prev) => ({
        ...initialComposer,
        assignee_email: prev.assignee_email,
        project_id: prev.project_id,
      }));
      loadTasks(scopeTab);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsSavingComposer(false);
    }
  };

  const toggleDone = async (task) => {
    const nextStatus = task.status === "completed" ? "in_progress" : "completed";
    const previousTasks = tasks;
    const previousDrawerTask = drawerTask;

    setTasks((prev) => prev.map((row) => (row.id === task.id ? { ...row, status: nextStatus } : row)));
    if (drawerTask?.id === task.id) {
      setDrawerTask((prev) => (prev ? { ...prev, status: nextStatus } : prev));
    }

    try {
      await updateTaskV2(task.id, { status: nextStatus });
      if (drawerTask?.id === task.id) {
        const remarks = await getTaskRemarks(task.id);
        setDrawerRemarks(remarks);
      }
    } catch (error) {
      setTasks(previousTasks);
      if (previousDrawerTask?.id === task.id) {
        setDrawerTask(previousDrawerTask);
      }
      toast.error(error.message);
    }
  };

  const loadDrawerRemarks = async (taskId) => {
    try {
      const rows = await getTaskRemarks(taskId);
      setDrawerRemarks(rows);
    } catch (_error) {
      setDrawerRemarks([]);
    }
  };

  const openDrawer = (task) => {
    setIsDrawerEditMode(false);
    setDrawerTask(task);
    setDrawerDraft({
      task: task.task,
      status: task.status,
      priority: task.priority || "medium",
      assigned_to_email: task.assigned_to,
      deadline: task.deadline,
      notes: task.notes || "",
    });
    setDrawerNoteDraft("");
    setMentionDropdownOpen(false);
    loadDrawerRemarks(task.id);
  };

  const syncDrawerTaskFromList = (taskId, rows) => {
    const nextTask = rows.find((row) => row.id === taskId);
    if (!nextTask) return;
    setDrawerTask(nextTask);
    setDrawerDraft((prev) => ({
      ...prev,
      task: nextTask.task,
      status: nextTask.status,
      priority: nextTask.priority || "medium",
      assigned_to_email: nextTask.assigned_to,
      deadline: nextTask.deadline,
      notes: nextTask.notes || prev.notes,
    }));
  };

  const refreshTasksAndDrawer = async () => {
    const rows = await getAllTasks({ assigned_scope: scopeTab, limit: 1000 });
    setTasks(rows);
    if (drawerTask) {
      syncDrawerTaskFromList(drawerTask.id, rows);
    }
  };

  const patchDrawerField = async (updates, successMessage = "Updated") => {
    if (!drawerTask) return;
    try {
      await updateTaskV2(drawerTask.id, updates);
      if (successMessage) toast.success(successMessage);
      await refreshTasksAndDrawer();
      await loadDrawerRemarks(drawerTask.id);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDrawerStatusChange = async (value) => {
    setDrawerDraft((prev) => ({ ...prev, status: value }));
    await patchDrawerField({ status: value }, value === "completed" ? "Task marked done" : "Status updated");
  };

  const handleDrawerDueDateChange = async (nextDate) => {
    setDrawerDraft((prev) => ({ ...prev, deadline: nextDate }));
    if (drawerTask && nextDate && nextDate !== drawerTask.deadline) {
      await patchDrawerField({ deadline: nextDate }, "Due date updated");
    }
  };

  const insertMentionAtCursor = (mentionText) => {
    const textarea = noteTextareaRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart ?? drawerNoteDraft.length;
    const before = drawerNoteDraft.slice(0, start);
    const after = drawerNoteDraft.slice(start);
    const beforeWithMention = before.replace(/@([A-Za-z0-9_-]*)$/, mentionText);
    const nextValue = `${beforeWithMention} ${after}`;
    setDrawerNoteDraft(nextValue);
    setMentionDropdownOpen(false);
    requestAnimationFrame(() => {
      const cursor = beforeWithMention.length + 1;
      textarea.focus();
      textarea.setSelectionRange(cursor, cursor);
    });
  };

  const handleNoteDraftChange = (value) => {
    setDrawerNoteDraft(value);
    const textarea = noteTextareaRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart ?? value.length;
    const textBeforeCursor = value.slice(0, start);
    const match = textBeforeCursor.match(/(?:^|\s)@([A-Za-z0-9_-]*)$/);
    if (match) {
      const anchor = mentionAnchorRef.current || textarea;
      const rect = anchor.getBoundingClientRect();
      setMentionDropdownPos({ top: rect.top - 8, left: rect.left, width: Math.max(260, rect.width) });
      setMentionQuery(match[1] || "");
      setMentionDropdownOpen(true);
      return;
    }
    setMentionDropdownOpen(false);
    setMentionQuery("");
  };

  const submitDrawerNote = async () => {
    if (!drawerTask) return;
    const message = drawerNoteDraft.trim();
    if (!message) return;
    setIsPostingRemark(true);
    try {
      await addTaskRemarkV2(drawerTask.id, {
        remark: message,
        mentions: mentionNamesFromText(message),
      });
      setDrawerNoteDraft("");
      setMentionDropdownOpen(false);
      await loadDrawerRemarks(drawerTask.id);
      await refreshTasksAndDrawer();
      toast.success("Note posted");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsPostingRemark(false);
    }
  };

  const saveDrawer = async () => {
    if (!drawerTask) return;
    try {
      await updateTaskV2(drawerTask.id, {
        task: drawerDraft.task,
        priority: drawerDraft.priority,
        assigned_to_email: drawerDraft.assigned_to_email,
        notes: drawerDraft.notes,
      });
      toast.success("Task updated");
      await refreshTasksAndDrawer();
      await loadDrawerRemarks(drawerTask.id);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const removeTask = async () => {
    if (!drawerTask) return;
    try {
      await deleteTaskV2(drawerTask.id);
      toast.success("Task removed");
      setDrawerTask(null);
      loadTasks(scopeTab);
    } catch (error) {
      toast.error(error.message);
    }
  };

  return (
    <AppShell user={user} onLogout={onLogout}>
      <section className="space-y-4" data-testid="tasks-page-root">
        <div className="jump-card flex flex-wrap items-center gap-4 px-4 py-3" data-testid="tasks-stats-strip">
          <div className="flex items-baseline gap-1"><span className="text-2xl font-bold" data-testid="tasks-open-count">{taskStats.open}</span><span className="text-xs text-slate-500">open</span></div>
          <div className="h-7 w-px bg-slate-200" />
          <p className="text-sm text-rose-700" data-testid="tasks-overdue-count">{taskStats.overdue} overdue</p>
          <p className="text-sm text-amber-700" data-testid="tasks-due-week-count">{taskStats.dueWeek} due this week</p>
          <p className="text-sm text-emerald-700" data-testid="tasks-on-track-count">{taskStats.onTrack} on track</p>
        </div>

        <div className="jump-card composer-wrap p-4" style={{ overflow: "visible", borderColor: "#C7D2FE", boxShadow: "0 0 0 2px rgba(79,70,229,.06) inset" }} data-testid="tasks-inline-composer">
          <div className="mb-3 flex items-start gap-2">
            <span className="mt-1 grid h-8 w-8 place-items-center rounded-full" style={{ background: "#EEF2FF", color: "#4F46E5", fontWeight: 700 }}>+</span>
            <div className="flex-1">
              <TaggingInput
                value={composer.title_markup}
                onChange={(value) => setComposer((prev) => ({ ...prev, title_markup: value }))}
                workspaceDomain={user.workspace_domain}
                placeholder="Type task title"
                testId="tasks-inline-composer-title-input"
              />
            </div>
          </div>

          <div className="composer-meta" style={{ display: "flex", flexWrap: "wrap", gap: 8 }} data-testid="tasks-inline-composer-meta">
            <Select value={composer.priority} onValueChange={(value) => setComposer((prev) => ({ ...prev, priority: value }))}>
              <SelectTrigger className="field-select" style={{ width: "auto", minWidth: 124, height: 34 }} data-testid="tasks-inline-priority-select">
                <SelectValue placeholder="Priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="high" data-testid="tasks-inline-priority-high">High</SelectItem>
                <SelectItem value="medium" data-testid="tasks-inline-priority-medium">Medium</SelectItem>
                <SelectItem value="low" data-testid="tasks-inline-priority-low">Low</SelectItem>
              </SelectContent>
            </Select>

            <div className="field-input" style={{ minWidth: 118, height: 34, display: "inline-flex", alignItems: "center", justifyContent: "center" }} data-testid="tasks-inline-status-fixed-wrap">
              <span className="text-sm text-slate-700" data-testid="tasks-inline-status-fixed-chip">Open</span>
            </div>

            <Input
              className="field-input"
              style={{ width: 150, height: 34 }}
              type="date"
              value={composer.due_date}
              onChange={(event) => setComposer((prev) => ({ ...prev, due_date: event.target.value }))}
              min={new Date().toISOString().slice(0, 10)}
              data-testid="tasks-inline-due-input"
            />

            <Select value={composer.assignee_email || ""} onValueChange={(value) => setComposer((prev) => ({ ...prev, assignee_email: value }))}>
              <SelectTrigger className="meta-pill" style={{ width: "auto", minWidth: 150, height: 34 }} data-testid="tasks-inline-assignee-select"><SelectValue placeholder="Assign to" /></SelectTrigger>
              <SelectContent>
                {composer.project_id === GENERAL_TASK_PROJECT_ID ? (
                  <SelectItem value="__all__" data-testid="tasks-inline-assignee-option-all">Assign to all</SelectItem>
                ) : null}
                {users.map((member) => (
                  <SelectItem key={member.email} value={member.email} data-testid={`tasks-inline-assignee-option-${member.email.replace(/[@.]/g, "-")}`}>
                    {member.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={composer.project_id || ""} onValueChange={(value) => setComposer((prev) => ({ ...prev, project_id: value }))}>
              <SelectTrigger className="meta-pill" style={{ width: "auto", minWidth: 190, height: 34 }} data-testid="tasks-inline-project-select"><SelectValue placeholder="General (Non-project)" /></SelectTrigger>
              <SelectContent>
                {projects.map((project) => (
                  <SelectItem key={project.id} value={project.id} data-testid={`tasks-inline-project-option-${project.id}`}>
                    <div className="flex w-full items-center justify-between gap-2" data-testid={`tasks-inline-project-option-content-${project.id}`}>
                      <span className="truncate">{project.name}</span>
                      <span
                        className="rounded-full border border-slate-200 bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-600"
                        data-testid={`tasks-inline-project-option-client-pill-${project.id}`}
                      >
                        {project.client_name || "Internal"}
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="mt-3 flex flex-wrap items-center justify-between gap-3" data-testid="tasks-inline-composer-actions">
            <p className="text-[11px] text-slate-500">Enter posts task quickly</p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setComposer(initialComposer)} data-testid="tasks-inline-cancel-button">Cancel</Button>
              <Button onClick={createInlineTask} disabled={isSavingComposer} style={{ background: "#111827", borderColor: "#111827", color: "#fff" }} data-testid="tasks-inline-submit-button">
                {isSavingComposer ? "Adding..." : "Add Task"}
              </Button>
            </div>
          </div>
        </div>

        <div className="jump-card flex flex-wrap items-center gap-2 p-3" data-testid="tasks-filter-bar">
          <span className="inline-flex items-center gap-1 text-xs text-slate-500"><ListFilter className="h-3.5 w-3.5" />Filters</span>
          {[
            ["all", "All"],
            ["assigned", "Assigned to me"],
            ["overdue", "Overdue"],
            ["high", "High priority"],
            ["created", "Created by me"],
          ].map(([key, label]) => (
            <button
              key={key}
              className={`jump-pill-button ${activeFilter === key ? "active" : ""}`}
              onClick={() => setActiveFilter(key)}
              data-testid={`tasks-filter-${key}`}
            >
              {label}
            </button>
          ))}

          <div className="ml-auto inline-flex items-center gap-2" data-testid="tasks-sort-wrap">
            <button className={`jump-pill-button ${activeSort === "due_date" ? "active" : ""}`} onClick={() => setActiveSort("due_date")} data-testid="tasks-sort-due-date">Sort: Due date</button>
            <button className={`jump-pill-button ${activeSort === "priority" ? "active" : ""}`} onClick={() => setActiveSort("priority")} data-testid="tasks-sort-priority">Sort: Priority</button>
          </div>

          <div className="inline-flex rounded-xl border border-slate-200 bg-white p-1" data-testid="tasks-scope-toggle">
            <button className={`rounded-lg px-3 py-1.5 text-xs font-semibold ${scopeTab === "all" ? "bg-slate-900 text-white" : "text-slate-600"}`} onClick={() => setScopeTab("all")} data-testid="tasks-scope-all">All</button>
            <button className={`rounded-lg px-3 py-1.5 text-xs font-semibold ${scopeTab === "assigned_to_you" ? "bg-slate-900 text-white" : "text-slate-600"}`} onClick={() => setScopeTab("assigned_to_you")} data-testid="tasks-scope-assigned-to-you">Assigned to me</button>
            <button className={`rounded-lg px-3 py-1.5 text-xs font-semibold ${scopeTab === "assigned_to_others" ? "bg-slate-900 text-white" : "text-slate-600"}`} onClick={() => setScopeTab("assigned_to_others")} data-testid="tasks-scope-assigned-to-others">Assigned to others</button>
          </div>
        </div>

        {isLoading ? (
          <div className="jump-card p-4 text-sm text-slate-500" data-testid="tasks-loading-state">Loading tasks...</div>
        ) : groupedTasks.length === 0 && completedTasksLast30Days.length === 0 ? (
          <div className="jump-card p-4 text-sm text-slate-500" data-testid="tasks-empty-state">No tasks match this filter.</div>
        ) : (
          groupedTasks.map((group) => {
            const isExpanded = expandedGroups[group.project_id] !== false;
            const groupHealth = group.health_score || 0;
            const groupHealthLabel = groupHealth < 50 ? "Critical" : groupHealth < 65 ? "At Risk" : groupHealth < 80 ? "Watch" : "Healthy";
            return (
            <section key={group.project_id} className="overflow-hidden rounded-xl border border-slate-200 bg-white" data-testid={`tasks-group-${group.project_id}`}>
              <div className="group-header flex items-center gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2" data-testid={`tasks-group-header-${group.project_id}`}>
                <button
                  type="button"
                  onClick={() => setExpandedGroups((prev) => ({ ...prev, [group.project_id]: !isExpanded }))}
                  className="inline-flex h-5 w-5 items-center justify-center rounded border border-slate-200 bg-white text-slate-500"
                  data-testid={`tasks-group-toggle-${group.project_id}`}
                >
                  {isExpanded ? "▾" : "▸"}
                </button>
                <span className={`dot ${group.overdue_count > 0 ? "dot-crit" : "dot-ok"}`} />
                <span className="font-semibold text-slate-900">{group.project_name}</span>
                <span className="text-xs text-slate-500">{group.client_name}</span>
                <span className="ml-auto jump-chip">{group.rows.length} tasks</span>
                {group.overdue_count > 0 ? <span className="jump-chip jump-chip-critical" data-testid={`tasks-group-overdue-${group.project_id}`}>{group.overdue_count} overdue</span> : null}
                <span className={`jump-chip ${groupHealth < 50 ? "jump-chip-critical" : groupHealth < 65 ? "jump-chip-risk" : groupHealth < 80 ? "jump-chip-watch" : "jump-chip-healthy"}`} data-testid={`tasks-group-health-${group.project_id}`}>
                  {groupHealth} {groupHealthLabel}
                </span>
              </div>

              <div className="overflow-x-auto" style={{ display: isExpanded ? undefined : "none" }}>
                <table className="jump-table" data-testid={`tasks-group-table-${group.project_id}`}>
                  <thead>
                    <tr>
                      <th>Done</th>
                      <th>Task</th>
                      <th>Assignee</th>
                      <th>Priority</th>
                      <th>Status</th>
                      <th>Due</th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.rows.map((task) => {
                      const assigneeLabel = memberLabelMap.get(task.assigned_to) || displayActor(task.assigned_to || "Unassigned");
                      const dueDelta = daysDeltaLabel(task.deadline);
                      return (
                      <tr key={task.id} onClick={() => openDrawer(task)} className="cursor-pointer" data-testid={`tasks-row-${task.id}`}>
                        <td>
                          <button
                            className={`tasks-done-toggle ${task.status === "completed" ? "is-done" : ""}`}
                            title="Mark done"
                            onClick={(event) => {
                              event.stopPropagation();
                              toggleDone(task);
                            }}
                            data-testid={`tasks-row-done-toggle-${task.id}`}
                          >
                            {task.status === "completed" ? (
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12l5 5L20 7" /></svg>
                            ) : null}
                          </button>
                        </td>
                        <td>
                          <p className={`task-title ${task.status === "completed" ? "is-done" : ""}`} data-testid={`tasks-row-title-${task.id}`}>{task.task}</p>
                        </td>
                        <td data-testid={`tasks-row-assignee-${task.id}`}>
                          <span className="inline-flex items-center gap-2">
                            <span className="avatar avatar-sm avatar-d">{assigneeLabel.slice(0, 2).toUpperCase()}</span>
                            <span>{assigneeLabel}</span>
                          </span>
                        </td>
                        <td>
                          <button type="button" className={rowPriorityClass[task.priority || "medium"]} data-testid={`tasks-row-priority-${task.id}`}>
                            <span style={{ width: 7, height: 7, borderRadius: 99, background: (priorityOptionMeta[task.priority || "medium"] || priorityOptionMeta.medium).dot, display: "inline-block" }} />
                            {(priorityOptionMeta[task.priority || "medium"] || priorityOptionMeta.medium).label}
                          </button>
                        </td>
                        <td>
                          <span className={statusChipClass[task.status] || statusChipClass.open} data-testid={`tasks-row-status-${task.id}`} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                            <span style={{ width: 7, height: 7, borderRadius: 99, background: (statusOptionMeta[task.status] || statusOptionMeta.open).dot, display: "inline-block" }} />
                            {(statusOptionMeta[task.status] || statusOptionMeta.open).label}
                          </span>
                        </td>
                        <td className={isOverdue(task) ? "task-due overdue" : "task-due"} data-testid={`tasks-row-due-${task.id}`}>
                          <div>{formatDisplayDate(task.deadline)}</div>
                          <div className="text-[11px]" style={{ color: isOverdue(task) ? "#B91C1C" : "#64748B" }}>{dueDelta}</div>
                        </td>
                      </tr>
                    );})}
                  </tbody>
                </table>
              </div>
            </section>
          );})
        )}

        {completedTasksLast30Days.length > 0 ? (
          <section className="overflow-hidden rounded-xl border border-slate-200 bg-white" data-testid="tasks-completed-section">
            <div className="group-header flex items-center gap-2 border-b border-slate-200 bg-emerald-50 px-3 py-2" data-testid="tasks-completed-header">
              <span className="dot dot-ok" />
              <span className="font-semibold text-slate-900">Completed (last 30 days)</span>
              <span className="ml-auto jump-chip jump-chip-healthy" data-testid="tasks-completed-count-chip">{completedTasksLast30Days.length} tasks</span>
            </div>

            <div className="space-y-4 p-3" data-testid="tasks-completed-groups">
              {completedGroups.map((group) => (
                <div key={group.project_id} className="rounded-xl border border-slate-200" data-testid={`tasks-completed-group-${group.project_id}`}>
                  <div className="flex items-center gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2">
                    <span className="font-semibold text-slate-900" data-testid={`tasks-completed-group-name-${group.project_id}`}>{group.project_name}</span>
                    <span className="text-xs text-slate-500" data-testid={`tasks-completed-group-client-${group.project_id}`}>{group.client_name}</span>
                    <span className="ml-auto text-xs text-slate-500" data-testid={`tasks-completed-group-count-${group.project_id}`}>{group.rows.length} completed</span>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="jump-table" data-testid={`tasks-completed-table-${group.project_id}`}>
                      <thead>
                        <tr>
                          <th>Task</th>
                          <th>Assignee</th>
                          <th>Completed on</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {group.rows.map((task) => {
                          const assigneeLabel = memberLabelMap.get(task.assigned_to) || displayActor(task.assigned_to || "Unassigned");
                          const completedAt = getTaskCompletionReference(task);
                          return (
                            <tr key={task.id} onClick={() => openDrawer(task)} className="cursor-pointer" data-testid={`tasks-completed-row-${task.id}`}>
                              <td>
                                <p className="task-title is-done" data-testid={`tasks-completed-title-${task.id}`}>{task.task}</p>
                              </td>
                              <td data-testid={`tasks-completed-assignee-${task.id}`}>{assigneeLabel}</td>
                              <td data-testid={`tasks-completed-date-${task.id}`}>{formatTaskDateTime(completedAt)}</td>
                              <td>
                                <button
                                  type="button"
                                  className="btn btn-sm"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    toggleDone(task);
                                  }}
                                  data-testid={`tasks-completed-restore-${task.id}`}
                                >
                                  Restore
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          </section>
        ) : null}

        <div className={`fixed inset-0 z-40 transition ${drawerTask ? "pointer-events-auto" : "pointer-events-none"}`} data-testid="tasks-drawer-wrap">
          <div className={`absolute inset-0 bg-slate-900/30 transition ${drawerTask ? "opacity-100" : "opacity-0"}`} onClick={() => setDrawerTask(null)} data-testid="tasks-drawer-backdrop" />
          <aside className={`absolute right-0 top-0 flex h-full w-full max-w-lg transform flex-col bg-white shadow-2xl transition ${drawerTask ? "translate-x-0" : "translate-x-full"}`} data-testid="tasks-drawer">
            <div className="border-b border-slate-200 px-4 py-3">
              <div className="mb-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <button onClick={() => setDrawerTask(null)} className="grid h-8 w-8 place-items-center rounded-full border border-slate-200 text-slate-600" data-testid="tasks-drawer-close-button"><X className="h-4 w-4" /></button>
                  <span className="jump-chip" data-testid="tasks-drawer-id-chip">TASK</span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setIsDrawerEditMode((prev) => !prev)}
                    className="grid h-8 w-8 place-items-center rounded border border-slate-200 text-slate-600"
                    data-testid="tasks-drawer-edit-button"
                  >
                    ✎
                  </button>
                  <button type="button" onClick={removeTask} className="grid h-8 w-8 place-items-center rounded border border-rose-200 text-rose-600" data-testid="tasks-drawer-delete-button-top">🗑</button>
                </div>
              </div>

              <p className="text-base font-semibold text-slate-900" data-testid="tasks-drawer-title">{drawerTask?.task || "Task"}</p>
              <p className="text-xs text-slate-500" data-testid="tasks-drawer-subtitle">{drawerTask?.project_id === GENERAL_TASK_PROJECT_ID ? "General Task" : drawerTask?.project_name || "Project task"}</p>
            </div>

            {drawerTask ? (
              <div className="h-[calc(100vh-96px)] overflow-y-auto" data-testid="tasks-drawer-content-scroll-wrap">
                <div className="space-y-4 p-4" data-testid="tasks-drawer-content">
                {drawerTask.status === "completed" && drawerTask.closed_at ? (
                  <div
                    className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800"
                    data-testid="tasks-drawer-completion-stamp"
                  >
                    <strong>Completed</strong> · By {displayActor(drawerTask.closed_by || drawerTask.assigned_by || user.email)} · {formatTaskDateTime(drawerTask.closed_at)}
                  </div>
                ) : null}

                {isDrawerEditMode ? (
                  <Input value={drawerDraft.task} onChange={(event) => setDrawerDraft((prev) => ({ ...prev, task: event.target.value }))} data-testid="tasks-drawer-task-input" />
                ) : null}

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="mb-1 text-xs text-slate-500">Status</p>
                    {isDrawerEditMode ? (
                      <Select value={drawerDraft.status} onValueChange={handleDrawerStatusChange}>
                        <SelectTrigger data-testid="tasks-drawer-status-select">
                          <MetaChip item={statusOptionMeta[drawerDraft.status] || statusOptionMeta.open} testId="tasks-drawer-status-selected-chip" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="open" data-testid="tasks-drawer-status-open"><MetaChip item={statusOptionMeta.open} testId="tasks-drawer-status-option-open" /></SelectItem>
                          <SelectItem value="in_progress" data-testid="tasks-drawer-status-progress"><MetaChip item={statusOptionMeta.in_progress} testId="tasks-drawer-status-option-in-progress" /></SelectItem>
                          <SelectItem value="blocked" data-testid="tasks-drawer-status-blocked"><MetaChip item={statusOptionMeta.blocked} testId="tasks-drawer-status-option-blocked" /></SelectItem>
                          <SelectItem value="completed" data-testid="tasks-drawer-status-completed"><MetaChip item={statusOptionMeta.completed} testId="tasks-drawer-status-option-completed" /></SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm" data-testid="tasks-drawer-status-readonly">
                        <MetaChip item={statusOptionMeta[drawerDraft.status] || statusOptionMeta.open} testId="tasks-drawer-status-readonly-chip" />
                      </div>
                    )}
                  </div>
                  <div>
                    <p className="mb-1 text-xs text-slate-500">Priority</p>
                    {isDrawerEditMode ? (
                      <Select value={drawerDraft.priority} onValueChange={(value) => setDrawerDraft((prev) => ({ ...prev, priority: value }))}>
                        <SelectTrigger data-testid="tasks-drawer-priority-select">
                          <MetaChip item={priorityOptionMeta[drawerDraft.priority] || priorityOptionMeta.medium} testId="tasks-drawer-priority-selected-chip" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="high" data-testid="tasks-drawer-priority-high"><MetaChip item={priorityOptionMeta.high} testId="tasks-drawer-priority-option-high" /></SelectItem>
                          <SelectItem value="medium" data-testid="tasks-drawer-priority-medium"><MetaChip item={priorityOptionMeta.medium} testId="tasks-drawer-priority-option-medium" /></SelectItem>
                          <SelectItem value="low" data-testid="tasks-drawer-priority-low"><MetaChip item={priorityOptionMeta.low} testId="tasks-drawer-priority-option-low" /></SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm" data-testid="tasks-drawer-priority-readonly">
                        <MetaChip item={priorityOptionMeta[drawerDraft.priority] || priorityOptionMeta.medium} testId="tasks-drawer-priority-readonly-chip" />
                      </div>
                    )}
                  </div>
                  <div>
                    <p className="mb-1 text-xs text-slate-500">Assignee</p>
                    {isDrawerEditMode ? (
                      <Select value={drawerDraft.assigned_to_email} onValueChange={(value) => setDrawerDraft((prev) => ({ ...prev, assigned_to_email: value }))}>
                        <SelectTrigger data-testid="tasks-drawer-assignee-select"><SelectValue placeholder="Assignee" /></SelectTrigger>
                        <SelectContent>
                          {users.map((member) => (
                            <SelectItem key={member.email} value={member.email} data-testid={`tasks-drawer-assignee-option-${member.email.replace(/[@.]/g, "-")}`}>
                              {member.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm" data-testid="tasks-drawer-assignee-readonly">{memberLabelMap.get(drawerDraft.assigned_to_email) || displayActor(drawerDraft.assigned_to_email)}</div>}
                  </div>
                  <div>
                    <p className="mb-1 text-xs text-slate-500">Due date</p>
                    {isDrawerEditMode ? (
                      <Input type="date" value={drawerDraft.deadline} onChange={(event) => handleDrawerDueDateChange(event.target.value)} data-testid="tasks-drawer-deadline-input" />
                    ) : <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm" data-testid="tasks-drawer-deadline-readonly">{formatDisplayDate(drawerDraft.deadline)}</div>}
                  </div>
                </div>

                <div className="rounded-xl border border-slate-200 p-3" data-testid="tasks-drawer-due-date-history">
                  <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-800">
                    <CalendarClock className="h-4 w-4" /> Due Date History
                  </div>
                  {dueDateHistory.length === 0 ? (
                    <p className="text-xs text-slate-500" data-testid="tasks-drawer-due-date-history-empty">No changes — original due date applies</p>
                  ) : (
                    <div className="space-y-1.5" data-testid="tasks-drawer-due-date-history-list">
                      {dueDateHistory.map((entry, index) => (
                        <div
                          key={`${entry.changed_at}-${index}`}
                          className="flex items-center justify-between text-xs"
                          data-testid={`tasks-drawer-due-date-history-item-${index}`}
                        >
                          <span className="inline-flex items-center gap-1.5 text-slate-700">
                            <span className={`h-2 w-2 rounded-full ${index === 0 ? "bg-indigo-500" : "bg-slate-300"}`} />
                            Changed from {entry.from || "—"} → {entry.to || "—"}
                          </span>
                          <span className="text-slate-500">{displayActor(entry.changed_by)} · {formatTaskDateTime(entry.changed_at)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <p className="mb-1 text-xs text-slate-500">Notes</p>
                  <Input value={drawerDraft.notes} onChange={(event) => setDrawerDraft((prev) => ({ ...prev, notes: event.target.value }))} data-testid="tasks-drawer-notes-input" />
                </div>

                <div className="rounded-xl border border-slate-200" data-testid="tasks-drawer-activity-thread">
                  <div className="border-b border-slate-200 px-3 py-2 text-sm font-semibold text-slate-800">Activity & Notes</div>
                  <div className="max-h-64 space-y-2 overflow-y-auto px-3 py-2" data-testid="tasks-drawer-activity-list">
                    {orderedRemarks.length === 0 ? (
                      <p className="text-xs text-slate-500" data-testid="tasks-drawer-activity-empty">No activity yet.</p>
                    ) : (
                      orderedRemarks.map((entry, index) => {
                        const kind = entry.kind || "note";
                        const createdAt = entry.created_at || entry.at;
                        if (kind === "system" || kind === "closing_remark") {
                          return (
                            <div key={entry.id || `${createdAt}-${index}`} className="rounded-lg bg-slate-100 px-2 py-1 text-xs text-slate-600" data-testid={`tasks-drawer-activity-system-${index}`}>
                              <span className="inline-flex items-center gap-1"><Clock3 className="h-3 w-3" /> {entry.remark}</span>
                              <span className="ml-1 text-[10px] text-slate-400">· {formatTaskDateTime(createdAt)}</span>
                            </div>
                          );
                        }
                        return (
                          <div key={entry.id || `${createdAt}-${index}`} className="rounded-lg border border-slate-200 bg-white px-3 py-2" data-testid={`tasks-drawer-activity-note-${index}`}>
                            <div className="mb-1 flex items-center gap-2 text-xs text-slate-500">
                              <span className="grid h-5 w-5 place-items-center rounded-full bg-slate-200 text-[10px] font-semibold text-slate-700">{displayActor(entry.by_email || entry.by).slice(0, 2).toUpperCase()}</span>
                              <span className="font-semibold text-slate-700">{displayActor(entry.by_email || entry.by)}</span>
                              <span>· {formatTaskDateTime(createdAt)}</span>
                            </div>
                            <p className="text-sm text-slate-700 whitespace-pre-wrap">{entry.remark}</p>
                          </div>
                        );
                      })
                    )}
                  </div>

                  <div className="border-t border-slate-200 px-3 py-2" ref={mentionAnchorRef} data-testid="tasks-drawer-note-composer">
                    <div className="mb-1 flex items-center justify-between">
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 text-xs text-slate-500"
                        onClick={() => {
                          const textarea = noteTextareaRef.current;
                          if (!textarea) return;
                          const cursor = textarea.selectionStart ?? drawerNoteDraft.length;
                          const next = `${drawerNoteDraft.slice(0, cursor)}@${drawerNoteDraft.slice(cursor)}`;
                          setDrawerNoteDraft(next);
                          textarea.focus();
                          setMentionDropdownOpen(true);
                          setMentionQuery("");
                        }}
                        data-testid="tasks-drawer-note-at-button"
                      >
                        <AtSign className="h-3.5 w-3.5" /> Mention
                      </button>
                      <button
                        type="button"
                        className="text-xs font-semibold text-slate-700"
                        onClick={submitDrawerNote}
                        disabled={isPostingRemark || !drawerNoteDraft.trim()}
                        data-testid="tasks-drawer-note-post-button"
                      >
                        {isPostingRemark ? "Posting..." : "Post"}
                      </button>
                    </div>
                    <textarea
                      ref={noteTextareaRef}
                      value={drawerNoteDraft}
                      onChange={(event) => handleNoteDraftChange(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" && !event.shiftKey) {
                          event.preventDefault();
                          submitDrawerNote();
                        }
                      }}
                      rows={1}
                      className="w-full resize-none rounded-lg border border-slate-200 px-2 py-2 text-sm outline-none focus:border-slate-400"
                      placeholder="Write a note… Use @ to mention"
                      data-testid="tasks-drawer-note-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2 text-xs text-slate-500">
                  <p data-testid="tasks-drawer-meta-project">Project: {drawerTask.project_name}</p>
                  <p data-testid="tasks-drawer-meta-created">Created by: {drawerTask.assigned_by}</p>
                </div>

                <div className="sticky bottom-0 flex items-center justify-between gap-2 border-t border-slate-200 bg-white pt-2" data-testid="tasks-drawer-actions-sticky">
                  <Button variant="outline" onClick={() => setDrawerTask(null)} data-testid="tasks-drawer-cancel-button">Close</Button>
                  {isDrawerEditMode ? <Button onClick={saveDrawer} data-testid="tasks-drawer-save-button">Save changes</Button> : null}
                </div>
              </div>
              </div>
            ) : null}
          </aside>
        </div>

        {mentionDropdownOpen && drawerTask
          ? createPortal(
              <div
                style={{
                  position: "fixed",
                  top: mentionDropdownPos.top,
                  left: mentionDropdownPos.left,
                  width: mentionDropdownPos.width,
                  maxHeight: 220,
                  overflowY: "auto",
                  zIndex: 120,
                  background: "#fff",
                  border: "1px solid var(--paper-200)",
                  borderRadius: 12,
                  boxShadow: "var(--shadow-pop)",
                  padding: 6,
                }}
                data-testid="tasks-drawer-mention-dropdown"
              >
                {filteredMentionMembers.map((member) => (
                  <button
                    key={member.id}
                    type="button"
                    onClick={() => insertMentionAtCursor(member.mention)}
                    style={{
                      display: "flex",
                      width: "100%",
                      alignItems: "center",
                      gap: 8,
                      padding: "8px 10px",
                      borderRadius: 8,
                      border: "none",
                      background: "transparent",
                      cursor: "pointer",
                      textAlign: "left",
                    }}
                    data-testid={`tasks-drawer-mention-option-${member.firstName.toLowerCase()}`}
                  >
                    <span className="avatar avatar-sm avatar-e">{member.firstName.slice(0, 2).toUpperCase()}</span>
                    <span style={{ fontSize: 13, color: "var(--ink-900)" }}>{member.mention} · {member.name}</span>
                  </button>
                ))}
              </div>,
              document.body,
            )
          : null}
      </section>
    </AppShell>
  );
}
